import { OnboardingStepFooter } from "@/features/onboarding/components/OnboardingStepFooter";
import { formComponents as fc } from "@/features/i18n/formComponents";
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@patient-watch/ui/form";
import { Input } from "@patient-watch/ui/input";
import usePatientMutation from "@/features/profile/api/usePatientMutation";
import { zodResolver } from "@hookform/resolvers/zod";
import dayjs from "dayjs";
import type React from "react";
import { useRef } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import * as z from "zod";
import { useProfileCopy } from "@/features/i18n/profileCopy";

/** Locale-aware date-of-birth schema; messages come from profileCopy. */
function makeDobSchema(copy: ReturnType<typeof useProfileCopy>) {
    return z.object({
        day: z.number().min(1, copy.dobDayRequired).max(31, copy.dobMaxDaysInMonth).optional(),
        month: z.number().min(1, copy.dobMonthRequired).max(12, copy.dobMonthsInYear).optional(),
        year: z.number().min(1900, copy.dobYearTooEarly).max(dayjs().year(), copy.dobYearInFuture).optional(),
    });
}

interface Props {
    userId: string;
    dateOfBirth: string | null;
    onSuccess?: () => void;
    onBack?: () => void;
    isDialog?: boolean;
    primaryLabel?: string;
}

const DateOfBirthForm = (props: Props) => {
    const copy = useProfileCopy();
    type FormSchemaType = z.infer<ReturnType<typeof makeDobSchema>>;

    const dayRef = useRef<HTMLInputElement>(null);
    const monthRef = useRef<HTMLInputElement>(null);
    const yearRef = useRef<HTMLInputElement>(null);

    const dobSchema = makeDobSchema(copy);
    const form = useForm<FormSchemaType>({
        resolver: zodResolver(dobSchema),
        defaultValues: {
            day: props.dateOfBirth ? dayjs(props.dateOfBirth).date() : undefined,
            month: props.dateOfBirth ? dayjs(props.dateOfBirth).month() + 1 : undefined,
            year: props.dateOfBirth ? dayjs(props.dateOfBirth).year() : undefined,
        },
    });

    const patientMutation = usePatientMutation(props.userId);

    const onSubmit: SubmitHandler<FormSchemaType> = async (formData) => {
        if (!formData.day || !formData.month || !formData.year) {
            return;
        }

        // Create date using UTC to avoid timezone issues
        const date_of_birth = new Date(Date.UTC(formData.year, formData.month - 1, formData.day)).toISOString();

        try {
            await patientMutation.mutateAsync({ date_of_birth });
            props.onSuccess?.();
        } catch {
            /* usePatientMutation shows toast on error */
        }
    };

    const handleInputChange = (
        event: React.ChangeEvent<HTMLInputElement>,
        field: { onChange: (value: number | undefined) => void },
        maxLength: number,
        nextRef?: React.RefObject<HTMLInputElement | null>
    ) => {
        let value = event.target.value;

        // Only allow numeric input from the start
        if (!/^\d*$/.test(value)) {
            // Find the last valid numeric string if invalid characters were added (e.g. paste)
            const match = value.match(/^(\d*)/);
            value = match ? match[0] : "";
        }

        // Ensure value does not exceed maxLength
        if (value.length > maxLength) {
            value = value.slice(0, maxLength);
        }

        if (value === "") {
            field.onChange(undefined);
        } else {
            // Convert to number if not empty
            field.onChange(Number(value));
        }

        // Auto-tab to next field if max length is reached
        if (value.length === maxLength && nextRef?.current) {
            nextRef.current.focus();
            nextRef.current.select();
        }
    };

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="form">
                <div className="flex flex-col gap-y-3">
                    <FormLabel>{copy.dateOfBirth}</FormLabel>
                    <div className="flex gap-x-2">
                        <div className="flex flex-[1_1_0]">
                            <FormField
                                control={form.control}
                                name="day"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormControl>
                                            <Input
                                                {...field}
                                                ref={dayRef}
                                                type="text"
                                                inputMode="numeric"
                                                placeholder={copy.dobPlaceholderDay}
                                                data-testid="dob-day-input"
                                                value={field.value?.toString() || ""}
                                                onChange={(event) => handleInputChange(event, field, 2, monthRef)}
                                                maxLength={2}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                        <div className="flex flex-[1_1_0]">
                            <FormField
                                control={form.control}
                                name="month"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormControl>
                                            <Input
                                                {...field}
                                                ref={monthRef}
                                                type="text"
                                                inputMode="numeric"
                                                placeholder={copy.dobPlaceholderMonth}
                                                data-testid="dob-month-input"
                                                value={field.value?.toString() || ""}
                                                onChange={(event) => handleInputChange(event, field, 2, yearRef)}
                                                // maxLength={2}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                        <div className="flex flex-[2_1_0]">
                            <FormField
                                control={form.control}
                                name="year"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormControl>
                                            <Input
                                                {...field}
                                                ref={yearRef}
                                                type="text"
                                                inputMode="numeric"
                                                placeholder={copy.dobPlaceholderYear}
                                                data-testid="dob-year-input"
                                                value={field.value?.toString() || ""}
                                                onChange={(event) => handleInputChange(event, field, 4)}
                                                maxLength={4}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                    </div>
                    <FormDescription>{copy.dateOfBirthFormatDescription}</FormDescription>
                </div>
                {props.isDialog ? (
                    <fc.SaveButton isDialog={props.isDialog} />
                ) : (
                    <OnboardingStepFooter
                        onBack={props.onBack}
                        disabled={patientMutation.isPending}
                        isLoading={patientMutation.isPending}
                        primaryLabel={props.primaryLabel}
                    />
                )}
            </form>
        </Form>
    );
};

export default DateOfBirthForm;
